import { Visibility, VisibilityOff } from '@mui/icons-material'
import Button from '@mui/material/Button'
import _ from 'lodash'
import { useContext, useEffect, useState } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SpaceBox } from 'components/Box'
import Filter, { FilterValue } from 'components/Filter'
import { SelectAutocomplete, StackForm } from 'components/Form'
import Loading from 'components/Loading'
import { SelectOption } from 'components/Select'
import { WorkspaceBidderContext } from 'features/globalSetup/contexts/WorkspaceBidderContext'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { Schema } from '../../form/schema'
import { DomainBidderOption } from '../../hooks/useGetDomainBiddersForSelect'
import { AdUnitsTable } from '../AdUnitsTable'
import { DomainBidderFields } from './sections/DomainBidderFields'
import { useDomainBidderData } from './useDomainBidderData'
import { getOptionalParamsCount, getRelatedDomainId, optionsMapper } from './utils'

type SchemaSlice = Pick<Schema, 'domainIds'>

export const FormBody = (): React.JSX.Element => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.bidders.edit.bidParamsAndConnection',
  })
  const { setCurrentDomainBidder, setValid } = useContext(WorkspaceBidderContext)
  const [tableFilters, setTableFilters] = useState<FilterValue[]>([])
  const [showOptionalParams, setShowOptionalParams] = useState<boolean>(false)

  const { domainBidder, loading, options } = useDomainBidderData()

  const { control, getValues, setValue } = useFormContext<SchemaSlice>()
  const {
    domainIds: { domainId },
  } = getValues()
  const formState = useFormState<SchemaSlice>()
  const { errors } = formState

  useEffect(() => {
    setTableFilters([])
  }, [domainId])

  useEffect(() => {
    if (setValid !== undefined) {
      setValid(_.isEmpty(errors))
    }
  }, [formState])

  if (!domainBidder || loading) {
    return <Loading />
  }

  const handleChangeDomain = (selectedOption: SelectOption<DomainBidderOption>): void => {
    if (setCurrentDomainBidder) {
      setCurrentDomainBidder(domainBidder)
    }

    setValue('domainIds.domainBidderId', selectedOption)
    setValue('domainIds.domainId', getRelatedDomainId(selectedOption.value, options))
  }

  const optionalParamsCount: number = getOptionalParamsCount(domainBidder)

  return (
    <>
      <StackForm sx={{ pb: CONTAINER_SPACING }}>
        <SelectAutocomplete<SchemaSlice, DomainBidderOption>
          control={control}
          label={t('domainSection.domain')}
          name='domainIds.domainBidderId'
          onChange={(selectedOption: SelectOption<DomainBidderOption>): void =>
            handleChangeDomain(selectedOption)
          }
          options={optionsMapper(options)}
        />

        <>
          <DomainBidderFields
            alerts={domainBidder.alerts}
            s2sAvailable={domainBidder.workspaceBidder.bidder.s2sAvailable}
            workspaceBidderPriority={domainBidder.workspaceBidder.priority}
          />

          <SpaceBox />
        </>
      </StackForm>

      <StackForm sx={{ justifyContent: 'space-between', pb: CONTAINER_SPACING }}>
        <Filter
          id='adUnitsTableFilter'
          onChange={setTableFilters}
          placeholder={t('filter')}
          values={tableFilters}
        />
        {optionalParamsCount > 0 && (
          <Button
            data-cy='toggle-optional-params-button'
            onClick={() => setShowOptionalParams(prev => !prev)}
            startIcon={showOptionalParams ? <VisibilityOff /> : <Visibility />}
          >
            {t(`optionalParams.${showOptionalParams ? 'hide' : 'show'}`, {
              count: optionalParamsCount,
            })}
          </Button>
        )}
      </StackForm>

      <AdUnitsTable
        domainBidder={domainBidder}
        filters={tableFilters}
        showOptionalParams={showOptionalParams}
      />
    </>
  )
}
